import {lazy, Suspense, useEffect, useMemo, useState} from "react";
import { AdaptableCard, Container } from "Components/shared";
import { injectReducer } from "Store";
import reducer from "./store";
import { useDispatch, useSelector } from "react-redux";
import { setImageSlider, setOosWithInfo, setOss } from "./store/productSlice";
import useResponsive from "Utils/hooks/useResponsive";
import { setDynamicStyles } from "Store/auth/authSlice";
import { liquidGetProduct } from "./store/thunks";
import { selectProduct, selectProductInfo } from "./store/productSelectors";
import isEqual from "fast-deep-equal/es6";
import ProductSkeleton from "Components/shared/loaders/ProductSkeleton";
import "swiper/css";
import classNames from "classnames";
import { setAddressActive, setCartId } from "../store/elementsSlice";
import AddressSection from "./components/AddressSection";
import PubSub, { Message } from "../../../Utils/pubSub";
import { TrackingService } from "../../../Services/TrackingService";
import UrlParams from "../../../Utils/UrlParams";
import {Spinner} from "../../../Components/ui";
import { liquidClient } from "../store/thunks";

injectReducer('product', reducer)

function ProductView({
  liquid,
  liquidToken,
  loading,
  groupingId,
  configs,
  dynamicStyles,
  productCols,
  address,
  addressActive,
  addressUpdated
}) {
  const dispatch = useDispatch()

  const product = useSelector(selectProduct, isEqual);
  const productInfo = useSelector(selectProductInfo, isEqual);

  const { seeMore, oos, oosWithInfo } = product
  const [isLoading, setIsLoading] = useState(false)
  const { text, general, components } = configs

  const { smaller } = useResponsive()

  useEffect(() => {
    dispatch(setImageSlider(smaller.sm));
    dispatch(setDynamicStyles({
      container: '100%',
      grid: !smaller.sm && general.details ? '2' : '1',
    }))
  }, [
    smaller,
    dispatch,
    seeMore,
    components.details,
    general.details
  ]);

  useEffect(() => {
    if (!product.loading) {
      TrackingService.trackViewItem(productInfo)
    }
  }, [product.loading, productInfo])

  useEffect(() => {
    // Initialize SDK if not ready, otherwise load product data
    if (!liquid && !loading && liquidToken !== '') {
      dispatch(liquidClient(liquidToken));
    } else if (liquid && groupingId) {
      dispatch(liquidGetProduct({ groupingId }))
    } else {
    }
  }, [liquid, groupingId, dispatch, loading, liquidToken])

  useEffect(() => {
    const params = new UrlParams(window.location.search)
    setIsLoading(params.get("demo") === "true")
    setTimeout(() => setIsLoading(false), 1000)
  }, [general.details]);

  useEffect(() => {
    const cartUpdateId = ({ cart }) => dispatch(setCartId(cart.id))
    PubSub.subscribe(Message.CartUpdated, cartUpdateId);
    PubSub.subscribe(Message.CartLoaded, cartUpdateId);
  }, [dispatch])

  const Element = useMemo(() => {
    if (!product.loading) return lazy(() => import('./Product'))
    return lazy(() => import("Components/shared/loaders/ProductSkeleton"))
  }, [product.loading])

  const outOfStock = oos || oosWithInfo

  if(outOfStock && !address.formattedAddress && !addressActive && !product.loading) {
    return <AddressSection
      text={text}
      general={general}
      address={{}}
      onClick={() => {
        dispatch(setAddressActive(true))
        dispatch(setOss(false))
        dispatch(setOosWithInfo(false))
      }}
    />
  }

  if(isLoading) {
    return <div className={"flex justify-center items-center"}>
      <Spinner color={text.headings.color} size={40}/>
    </div>
  }

  return (
    <Container
      className={classNames(
        `w-full h-full justify-center items-center flex`,
        { 'ml-0': !general.details,
        '!items-start': smaller.sm
        },
      )}
      style={{
        width: dynamicStyles.container,
        maxWidth: 'unset',
        fontFamily: text.body.font,
        color: text.body.color,
      }}
    >
      <AdaptableCard
        className={'gap-2 antialiased p-0 py-0 shadow-none relative rounded-none w-full'}
        style={{
          background: 'transparent',
          border: 'unset'
        }}
      >
        <div
          className={'grid gap-2 w-full'}
          style={{ gridTemplateColumns: `repeat(${smaller.sm ? 1 : productCols},minmax(0,1fr))` }}
        >
          <Suspense fallback={<ProductSkeleton productCols={productCols}/>}>
            <Element
              {...configs}
              product={product}
              oos={oos && !product.loading && !addressActive}
              oosWithInfo={oosWithInfo && !product.loading}
              address={address}
              addressActive={addressActive}
              addressUpdated={addressUpdated}
              dynamicStyles={dynamicStyles}
              productCols={productCols}
            />
          </Suspense>
        </div>
      </AdaptableCard>
    </Container>
  )
}


export default ProductView
